/**
 * RecommendationWorkflowGenerator - Admin Tool
 * Generate recommendation workflow tasks (1 parent + 6 subtasks)
 * from existing college_recommendation_letters records
 *
 * Auto-checks tasks based on recommendation phase/status
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { Users, Plus, Loader2, Sparkles, CheckCircle, AlertCircle, Mail, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function RecommendationWorkflowGenerator() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingRecs, setLoadingRecs] = useState(false);

  useEffect(() => {
    loadStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      loadRecommendations();
    } else {
      setRecommendations([]);
      setSelectedRecommendations([]);
    }
  }, [selectedStudent]);

  const loadStudents = async () => {
    try {
      setLoading(true);

      const { data: academicProfiles, error } = await supabase
        .from('student_academic_profiles')
        .select('user_id, graduation_year');

      if (error) throw error;

      const userIds = academicProfiles.map(p => p.user_id);
      const { data: familyMembers, error: familyError } = await supabase
        .from('family_members')
        .select('id, name, email')
        .in('id', userIds);

      if (familyError) throw familyError;

      const studentData = familyMembers.map(fm => {
        const profile = academicProfiles.find(p => p.user_id === fm.id);
        return {
          id: fm.id,
          name: fm.name,
          email: fm.email,
          graduationYear: profile?.graduation_year
        };
      });

      setStudents(studentData);
    } catch (error) {
      console.error('[RecommendationWorkflowGenerator] Error loading students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendations = async () => {
    try {
      setLoadingRecs(true);

      const { data: recs, error } = await supabase
        .from('college_recommendation_letters')
        .select('*')
        .eq('user_id', selectedStudent)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Check which recommendations already have tasks
      const recIds = recs.map(r => r.id);
      const { data: existingTasks, error: tasksError } = await supabase
        .from('timeline_items')
        .select('source_id')
        .eq('user_id', selectedStudent)
        .eq('source_type', 'recommendation')
        .in('source_id', recIds.map(id => `recommendation_${id}_parent`));

      if (tasksError) throw tasksError;

      const existingSourceIds = new Set(existingTasks.map(t => t.source_id));

      const recsWithStatus = recs.map(rec => ({
        ...rec,
        hasExistingTasks: existingSourceIds.has(`recommendation_${rec.id}_parent`),
        phase: determinePhase(rec)
      }));

      setRecommendations(recsWithStatus);
    } catch (error) {
      console.error('[RecommendationWorkflowGenerator] Error loading recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoadingRecs(false);
    }
  };

  const determinePhase = (rec) => {
    if (rec.submitted_date) return 'Submitted';
    if (rec.request_accepted) return 'Accepted - In Progress';
    if (rec.request_declined) return 'Declined';
    if (rec.request_sent) return 'Requested';
    if (rec.draft_completed) return 'Draft Complete';
    return 'Not Started';
  };

  const toggleRecommendation = (recId) => {
    setSelectedRecommendations(prev => {
      if (prev.includes(recId)) {
        return prev.filter(id => id !== recId);
      } else {
        return [...prev, recId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedRecommendations.length === recommendations.length) {
      setSelectedRecommendations([]);
    } else {
      setSelectedRecommendations(recommendations.map(r => r.id));
    }
  };

  const generateWorkflows = async () => {
    if (selectedRecommendations.length === 0) {
      toast.error('Please select at least one recommendation');
      return;
    }

    setGenerating(true);
    const toastId = toast.loading(`Generating workflows for ${selectedRecommendations.length} recommendation${selectedRecommendations.length !== 1 ? 's' : ''}...`);

    try {
      // STEP 1: Get templates
      const { data: allTemplates, error: templatesError } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('is_active', true)
        .eq('category', 'application')
        .eq('metadata->>workflow_type', 'recommendation')
        .order('created_at', { ascending: false }); // Most recent first

      if (templatesError) throw templatesError;

      console.log(`[RecommendationWorkflowGenerator] Found ${allTemplates.length} templates total`);

      // Handle duplicates - take the most recent template for each task_sequence
      const templatesBySequence = {};
      for (const template of allTemplates) {
        const seq = template.metadata?.task_sequence;
        if (seq && !templatesBySequence[seq]) {
          templatesBySequence[seq] = template; // First one is most recent due to ordering
        }
      }

      const templates = Object.values(templatesBySequence).sort((a, b) => a.metadata.task_sequence - b.metadata.task_sequence);

      if (templates.length !== 6) {
        console.error('[RecommendationWorkflowGenerator] Template mismatch:', {
          expected: 6,
          found: templates.length,
          totalInDb: allTemplates.length,
          sequences: templates.map(t => t.metadata?.task_sequence)
        });
        throw new Error(`Expected 6 unique recommendation templates (sequences 1-6), found ${templates.length}. Database has ${allTemplates.length} total templates. May need to run cleanup.`);
      }

      // Warn about duplicates
      if (allTemplates.length > 6) {
        const duplicateCount = allTemplates.length - 6;
        console.warn(`[RecommendationWorkflowGenerator] Found ${duplicateCount} duplicate templates. Using most recent for each sequence.`);
        toast(`⚠️ Found ${duplicateCount} duplicate templates - using most recent. Consider running cleanup.`, {
          duration: 5000,
          icon: '⚠️'
        });
      }

      console.log(`[RecommendationWorkflowGenerator] Using ${templates.length} templates after deduplication`);

      let successCount = 0;
      let errorCount = 0;

      // STEP 2: Process each selected recommendation
      for (const recId of selectedRecommendations) {
        try {
          const rec = recommendations.find(r => r.id === recId);
          if (!rec) continue;

          // Skip if already has tasks
          if (rec.hasExistingTasks) {
            console.log(`[RecommendationWorkflowGenerator] Skipping ${rec.recommender_name} - tasks already exist`);
            continue;
          }

          // Determine deadline
          const deadline = rec.due_date ? parseISO(rec.due_date) : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

          // Build parent task title
          // SYNC WITH: supabase/migrations/20251024_fix_task_generation_issues_phase1.sql (generate_recommendation_workflow_tasks)
          // This logic MUST match the trigger function exactly - if you change it there, change it here too
          const recommenderLabel = rec.recommender_type === 'teacher' && rec.subject
            ? `${rec.subject} Teacher`
            : rec.recommender_type === 'counselor' ? 'Counselor'
            : rec.recommender_type === 'coach' ? 'Coach'
            : rec.recommender_type === 'other' ? 'Recommender'
            : 'Recommender';

          const parentTitle = `Request from ${rec.recommender_name} - ${recommenderLabel}`;

          // Create parent task
          const parentTaskData = {
            user_id: selectedStudent,
            college_id: null,
            title: parentTitle,
            description: 'Complete recommendation workflow: identify → request → follow up → verify → thank',
            category: 'application',
            priority: 'high',
            due_date: new Date(deadline.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            scheduled_date: new Date(deadline.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            status: 'planned',
            source_type: 'recommendation',
            source_id: `recommendation_${rec.id}_parent`,
            is_subtask: false,
            step_count: 6,
            completed_step_count: 0,
            epic_id: 'applications',
            tags: ['recommendation_workflow', 'parent_task'],
            module_link: '/college/recommendations',

            // Workflow hierarchy fields (parent task is level 2 - sub-workflow)
            // SYNC WITH: supabase/migrations/20251024_fix_task_generation_issues_phase1.sql
            // Generate name-based key, NOT UUID-based (recommender_${rec.id} creates UUIDs)
            workflow_key: 'secure_recommendations',
            sub_workflow_key: 'rec_' + rec.recommender_name
              .toLowerCase()
              .replace(/[^a-zA-Z0-9\s]/g, '')
              .replace(/\s+/g, '_'),
            hierarchy_level: 2,
            display_order: 0,

            metadata: {
              recommendation_id: rec.id,
              recommender_name: rec.recommender_name,
              recommender_email: rec.recommender_email,
              recommender_type: rec.recommender_type,
              subject: rec.subject,
              is_parent_task: true,
              workflow_type: 'recommendation',
              generated_by: 'admin_tool',
              generated_at: new Date().toISOString()
            }
          };

          const { data: parentTask, error: parentError } = await supabase
            .from('timeline_items')
            .insert(parentTaskData)
            .select()
            .single();

          if (parentError) throw parentError;

          console.log(`[RecommendationWorkflowGenerator] Created parent task: ${parentTask.id}`);

          // STEP 3: Create 6 subtasks with status mapping
          let completedSubtasks = 0;

          for (const template of templates) {
            const offsetDays = template.offset_days || 0;
            const dueDate = new Date(deadline.getTime() + offsetDays * 24 * 60 * 60 * 1000);

            // Ensure due date is not in the past
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const taskDueDate = dueDate < today ? new Date(today.getTime() + 24 * 60 * 60 * 1000) : dueDate;

            // Determine task status based on recommendation phase
            const taskStatus = determineTaskStatus(rec, template.metadata?.task_sequence);
            if (taskStatus === 'completed') {
              completedSubtasks++;
            }

            // SYNC WITH: supabase/migrations/20251024_fix_task_generation_issues_phase1.sql
            // Must replace BOTH [RECOMMENDER_NAME] and [RECOMMENDER_TYPE] in title AND description
            const subtaskData = {
              user_id: selectedStudent,
              college_id: null,
              parent_task_id: parentTask.id,
              title: template.title
                .replace(/\[RECOMMENDER_NAME\]/g, rec.recommender_name)
                .replace(/\[RECOMMENDER TYPE\]/g, recommenderLabel)  // Space variant
                .replace(/\[RECOMMENDER_TYPE\]/g, recommenderLabel), // Underscore variant
              description: (template.description || '')
                .replace(/\[RECOMMENDER_NAME\]/g, rec.recommender_name)
                .replace(/\[RECOMMENDER_TYPE\]/g, recommenderLabel),
              category: 'application',
              priority: template.priority,
              due_date: taskDueDate.toISOString().split('T')[0],
              scheduled_date: taskDueDate.toISOString().split('T')[0],
              status: taskStatus,
              source_type: 'recommendation',
              source_id: `recommendation_${rec.id}_task_${template.metadata?.task_sequence}`,
              is_subtask: true,
              epic_id: 'applications',
              workflow_phase: 'ready',
              tags: ['recommendation_workflow', 'subtask'],
              module_link: template.module_link,

              // Workflow hierarchy fields (for workflow view)
              // SYNC WITH trigger - child tasks inherit parent's sub_workflow_key
              workflow_key: template.workflow_key || 'secure_recommendations',
              sub_workflow_key: 'rec_' + rec.recommender_name
                .toLowerCase()
                .replace(/[^a-zA-Z0-9\s]/g, '')
                .replace(/\s+/g, '_'),
              hierarchy_level: 3,
              display_order: template.display_order || template.metadata?.task_sequence || 0,
              workflow_metadata: template.workflow_metadata || {},

              // Additional template fields
              estimated_duration: template.estimated_duration || 60,
              intensity_level: template.intensity_level || null,
              is_foundation_task: template.is_foundation_template || false,
              unlock_description: template.unlock_description || null,
              can_autogenerate: template.can_autogenerate || null,
              autogen_quality: template.autogen_quality || null,
              task_tip: template.task_tip || null,

              metadata: {
                recommendation_id: rec.id,
                template_id: template.id,
                workflow_sequence: template.metadata?.task_sequence,
                recommender_name: rec.recommender_name,
                recommender_type: rec.recommender_type,
                subject: rec.subject,
                parent_task_id: parentTask.id,
                generated_by: 'admin_tool',
                generated_at: new Date().toISOString(),
                auto_completed: taskStatus === 'completed'
              }
            };

            const { error: subtaskError } = await supabase
              .from('timeline_items')
              .insert(subtaskData);

            if (subtaskError) {
              console.error(`[RecommendationWorkflowGenerator] Error creating subtask ${template.metadata?.task_sequence}:`, subtaskError);
            } else {
              console.log(`[RecommendationWorkflowGenerator] Created subtask ${template.metadata?.task_sequence}: ${template.title} (${taskStatus})`);
            }
          }

          // Update parent task with completed_step_count
          if (completedSubtasks > 0) {
            await supabase
              .from('timeline_items')
              .update({ completed_step_count: completedSubtasks })
              .eq('id', parentTask.id);
          }

          successCount++;
        } catch (error) {
          console.error(`[RecommendationWorkflowGenerator] Error processing recommendation:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully generated ${successCount} workflow${successCount !== 1 ? 's' : ''}`, {
          id: toastId,
          duration: 5000
        });
      }

      if (errorCount > 0) {
        toast.error(`Failed to generate ${errorCount} workflow${errorCount !== 1 ? 's' : ''}`, {
          duration: 5000
        });
      }

      // Reload recommendations to update status
      await loadRecommendations();
      setSelectedRecommendations([]);

    } catch (error) {
      console.error('[RecommendationWorkflowGenerator] Error:', error);
      toast.error(`Failed to generate workflows: ${error.message}`, {
        id: toastId,
        duration: 6000
      });
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Maps recommendation phase to task status
   * Task 1: Identify recommender (draft_completed)
   * Task 2: Request recommendation (request_sent)
   * Task 3: Provide materials (request_accepted)
   * Task 4: Follow up (follow_up_email_sent_date)
   * Task 5: Verify submission (submitted_date)
   * Task 6: Send thank you (submitted_date or status = 'complete')
   */
  const determineTaskStatus = (rec, taskSequence) => {
    switch (taskSequence) {
      case 1: // Identify recommender
        return rec.draft_completed ? 'completed' : 'planned';

      case 2: // Request recommendation
        return rec.request_sent ? 'completed' : 'planned';

      case 3: // Provide materials
        return rec.request_accepted ? 'completed' : 'planned';

      case 4: // Follow up
        return rec.follow_up_email_sent_date ? 'completed' : 'planned';

      case 5: // Verify submission
        return rec.submitted_date ? 'completed' : 'planned';

      case 6: // Send thank you
        return (rec.submitted_date || rec.status === 'complete') ? 'completed' : 'planned';

      default:
        return 'planned';
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'Submitted': return 'text-green-700 bg-green-100';
      case 'Accepted - In Progress': return 'text-blue-700 bg-blue-100';
      case 'Requested': return 'text-yellow-700 bg-yellow-100';
      case 'Draft Complete': return 'text-purple-700 bg-purple-100';
      case 'Declined': return 'text-red-700 bg-red-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-violet-600" />
          Recommendation Workflow Generator
        </h2>
        <p className="text-slate-600 text-sm mt-1">
          Generate recommendation workflow tasks from existing recommendation records
        </p>
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <strong>How it works:</strong> Select a student to see their recommendation letters,
          then select which ones to generate tasks for. Tasks will be auto-checked based on the recommendation's current phase.
        </div>
      </div>

      {/* Student Selection */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Student *
          </label>
          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
            disabled={loading || generating}
          >
            <option value="">Choose a student...</option>
            {students.map(student => (
              <option key={student.id} value={student.id}>
                {student.name} ({student.email}) - Class of {student.graduationYear}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Recommendations Table */}
      {selectedStudent && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              Recommendation Letters
              {recommendations.length > 0 && ` (${recommendations.length})`}
            </h3>
            {recommendations.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className="text-sm text-violet-600 hover:text-violet-700 font-medium"
                disabled={generating}
              >
                {selectedRecommendations.length === recommendations.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>

          {loadingRecs ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading recommendations...</span>
            </div>
          ) : recommendations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No recommendation letters found for this student</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Select
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recommender
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phase
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recommendations.map((rec) => (
                    <tr
                      key={rec.id}
                      className={`hover:bg-gray-50 ${
                        rec.hasExistingTasks ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRecommendations.includes(rec.id)}
                          onChange={() => toggleRecommendation(rec.id)}
                          disabled={generating || rec.hasExistingTasks}
                          className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">
                          {rec.recommender_name}
                        </div>
                        <div className="text-xs text-gray-500">{rec.recommender_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {rec.recommender_type || 'N/A'}
                        </div>
                        {rec.subject && (
                          <div className="text-xs text-gray-500">{rec.subject}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPhaseColor(rec.phase)}`}>
                          {rec.phase}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {rec.due_date ? format(parseISO(rec.due_date), 'MMM d, yyyy') : 'Not set'}
                      </td>
                      <td className="px-4 py-3">
                        {rec.hasExistingTasks ? (
                          <div className="flex items-center gap-1 text-xs text-green-700">
                            <CheckCircle className="w-3 h-3" />
                            <span>Tasks exist</span>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">No tasks</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Generate Button */}
      {selectedStudent && recommendations.length > 0 && (
        <button
          onClick={generateWorkflows}
          disabled={generating || selectedRecommendations.length === 0}
          className={`w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
            generating || selectedRecommendations.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-violet-600 hover:bg-violet-700 text-white shadow-sm hover:shadow'
          }`}
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Workflows...
            </>
          ) : (
            <>
              <Plus className="w-5 h-5" />
              Generate Tasks for {selectedRecommendations.length} Selected
            </>
          )}
        </button>
      )}

      {/* Info Box */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 text-sm">
        <div className="font-semibold text-slate-800">Task Auto-Completion Mapping:</div>
        <div className="space-y-1 text-slate-600">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
            <span><strong>Task 1 (Identify):</strong> Marked complete if draft_completed = true</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
            <span><strong>Task 2 (Request):</strong> Marked complete if request_sent = true</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
            <span><strong>Task 3 (Materials):</strong> Marked complete if request_accepted = true</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
            <span><strong>Task 4 (Follow-up):</strong> Marked complete if follow_up_email_sent_date exists</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
            <span><strong>Task 5 (Verify):</strong> Marked complete if submitted_date exists</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-600" />
            <span><strong>Task 6 (Thank you):</strong> Marked complete if submitted_date exists or status = 'complete'</span>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-300">
          <div className="flex items-start gap-2 text-blue-700">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <span>
              <strong>Note:</strong> Tasks are linked to recommendation records via source_id.
              Future status syncs can be implemented via trigger or scheduled job.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
